var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { FormikContext } from 'formik';
import AddressLookup from '../../AddressLookup/AddressLookup';
import Button from '../../Button/Button';
import { addressFields, SiteContext, errorMessages, inputValidationRegex, commonValidationSchemas, } from '../../../utils/common';
import useStyles from './AddressSearchForm.styles';
import Alert from '../../Alert/Alert';
import { billingAddressCountries } from '../../../utils/billingAddressCountries';
import Select from '../../Select/Select';
export const validationSchema = Yup.object({
    firstName: commonValidationSchemas.firstName,
    lastName: commonValidationSchemas.lastName,
    phone: commonValidationSchemas.phone,
    addressSelection: Yup.string()
        .min(3, errorMessages(4).minChars)
        .required(errorMessages('Address line 1').required)
        .oneOf([Yup.ref('originalAddressSelection'), null], "Please select an address from the list or choose 'enter manually'"),
    addressLine1: commonValidationSchemas.addressLine1,
    addressLine2: commonValidationSchemas.addressLine2,
    addressLine3: commonValidationSchemas.addressLine3,
    city: commonValidationSchemas.city,
    county: commonValidationSchemas.county,
    postalCode: commonValidationSchemas.postalCode,
    country: commonValidationSchemas.country,
    deliveryInstructionOption: Yup.string().matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    deliveryInstructionNote: Yup.string().matches(inputValidationRegex, errorMessages().unsupportedCharacters),
    defaultAddress: Yup.boolean(),
    shouldValidatePhone: Yup.boolean(),
});
const AddressSearchForm = (props) => {
    var _a, _b;
    const { isNewAddress, isContactAddress, onCancel, isSubmitting, hasFormError, trackEnterManualAddressClick } = props;
    const { classes } = useStyles();
    const formikContext = useContext(FormikContext);
    const setManualAddressChosen = (isManualAddress) => {
        !!formikContext && formikContext.setFieldValue('isManualAddress', isManualAddress);
        if (isManualAddress) {
            const click_location = isContactAddress ? ' Address Book' : 'Billing Address';
            trackEnterManualAddressClick && trackEnterManualAddressClick(click_location);
        }
    };
    const { isFlyingFlowers, getFormattedAddress, shorthand } = useContext(SiteContext);
    const isPhoneValidate = !(!!isFlyingFlowers || !isContactAddress);
    const [showSelector, setShowSelector] = useState(false);
    const codeAlphaCountry = shorthand === 'ie' ? 'IE' : 'GB';
    const codeAlpha2Default = ((_a = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _a === void 0 ? void 0 : _a.country) ? (_b = formikContext === null || formikContext === void 0 ? void 0 : formikContext.values) === null || _b === void 0 ? void 0 : _b.country : codeAlphaCountry;
    const [countryUpdate, setCountryUpdate] = useState(codeAlpha2Default);
    const countryList = (billingAddressCountries === null || billingAddressCountries === void 0 ? void 0 : billingAddressCountries.length)
        ? billingAddressCountries.map((country) => ({ label: country.name, value: country.codeAlpha2 }))
        : [];
    const handleAddressFormatCall = (suggestion, format) => __awaiter(void 0, void 0, void 0, function* () {
        if (!getFormattedAddress) {
            throw new Error('Format address callback not found in context');
        }
        else {
            const { data, error } = yield getFormattedAddress({
                input: {
                    suggestion,
                    format,
                    countryCode: countryBasedOnSelection === null || countryBasedOnSelection === void 0 ? void 0 : countryBasedOnSelection.codeAlpha2,
                },
            });
            if (error) {
                throw new Error(`Address formatting returned an error response: ${error}`);
            }
            else if (!data) {
                throw new Error('No results returned from address formatting');
            }
            return data.formatAddress;
        }
    });
    useEffect(() => {
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('shouldValidatePhone', isPhoneValidate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFlyingFlowers, formikContext === null || formikContext === void 0 ? void 0 : formikContext.values.shouldValidatePhone, isContactAddress]);
    useEffect(() => {
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('addressLine1', '');
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('county', '');
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('city', '');
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('addressLine2', '');
        formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('postalCode', '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const countryBasedOnSelection = useMemo(() => billingAddressCountries === null || billingAddressCountries === void 0 ? void 0 : billingAddressCountries.find((address) => address.codeAlpha2 === countryUpdate), [countryUpdate]);
    const handleClick = () => {
        setShowSelector(true);
    };
    const countrySelected = (e) => {
        !!formikContext && (formikContext === null || formikContext === void 0 ? void 0 : formikContext.setFieldValue('country', e.target.value));
        setCountryUpdate(e.target.value);
    };
    const placeHolder = () => {
        let displayMessage = '';
        if (shorthand === 'ie' && !isContactAddress) {
            displayMessage = 'Try the eircode or street name';
        }
        else if (isContactAddress) {
            displayMessage = 'Enter Delivery Postcode';
        }
        else {
            displayMessage = 'Enter Billing Postcode';
        }
        return displayMessage;
    };
    return (_jsxs(_Fragment, { children: [_jsxs("h2", { children: ["Find a ", !isNewAddress && 'different', " address"] }), !isContactAddress && _jsx("p", { children: "This is used as your default billing address in checkout." }), _jsxs("div", { className: classes.input, children: [!isFlyingFlowers && isContactAddress && (_jsx(_Fragment, { children: !showSelector ? (_jsxs(_Fragment, { children: [_jsx("h4", { className: classes.countryTitle, children: "Country" }), _jsxs("div", { className: classes.countryDisplay, children: [countryBasedOnSelection === null || countryBasedOnSelection === void 0 ? void 0 : countryBasedOnSelection.name, ' ', _jsx("button", { className: classes.linkText, onClick: () => handleClick(), children: "Change" })] })] })) : (_jsx("div", { className: classes.input, children: _jsx(Select, { value: countryUpdate, onChange: (ev) => countrySelected(ev), name: "country", label: "Choose a country", placeholder: "Select from list", items: countryList, required: true, fullWidth: true }) })) })), _jsx(AddressLookup, { name: "addressSelection", label: "Search for different address", placeholder: placeHolder(), addressFields: addressFields, setManualAddressChosen: setManualAddressChosen, handleAddressFormatCall: handleAddressFormatCall, provideManualAddress: true, countryCode: countryBasedOnSelection === null || countryBasedOnSelection === void 0 ? void 0 : countryBasedOnSelection.codeAlpha3, manualAddressTitle: "Enter address", required: true, fullWidth: true })] }), hasFormError && _jsx(Alert, { type: "alertError", message: "Sorry, the address has not passed validation" }), _jsxs("div", { className: classes.buttons, children: [_jsx("div", { className: classes.saveButton, children: _jsx(Button, { variant: "secondary", thin: true, fullWidth: true, title: isContactAddress ? "Save contact's address" : 'Save billing address', isSubmit: true, icon: isFlyingFlowers ? 'check' : undefined, disabled: isSubmitting }) }), _jsx(Button, { variant: "tertiary", thin: true, fullWidth: true, title: isContactAddress ? 'Cancel - discard these changes' : 'Cancel - discard changes', icon: isFlyingFlowers ? 'close' : undefined, onClick: onCancel })] })] }));
};
export default AddressSearchForm;
